import type { HookResult, RawParams, Transition } from "@uirouter/core";
import dayjs from "dayjs";

import categoryService from "app/common/services/categoryService";
import { swSettings } from "app/common/services/swSettings";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import DurationService from "app/services/DurationService";
import { hasLinesOfBusinessSegments } from "app/services/segments/LinesOfBusinessUtils";
import type { IRouterState } from "single-spa/react-app/router/IRouterState";

import { LEGACY_ROUTES, V2_ROUTES, type LegacyRoute, type V2Route } from "./routes";

export const extractCategoriesFromRouteParams = (params?: RawParams): string[] => {
    if (!params?.category) {
        return [];
    }

    if (Array.isArray(params.category)) {
        return (params.category as string[]).filter(Boolean);
    }

    // Handle single category string
    if (typeof params.category === "string") {
        // Check if it's a comma-separated string (transitions to legacy routes)
        if (params.category.includes(",")) {
            return params.category.split(",").filter(Boolean);
        }

        // Single category (legacy routes)
        return [params.category];
    }

    return [];
};

/**
 * Extracts the primary category from route parameters
 * Handles both single category (legacy) and array category (V2) formats
 */
export const extractPrimaryCategoryFromParams = (categoryParam: string | string[] | undefined): string | undefined => {
    if (!categoryParam) {
        return undefined;
    }

    // Handle array format (V2 routes) - return first category
    if (Array.isArray(categoryParam)) {
        return categoryParam[0];
    }

    // Handle single category (legacy routes)
    if (typeof categoryParam === "string") {
        return categoryParam;
    }

    return undefined;
};

/**
 * Route Type Guards and Checkers
 */
export const isLegacyRoute = (routeName?: string): routeName is LegacyRoute => {
    return Object.values(LEGACY_ROUTES).includes(routeName as LegacyRoute);
};

export const isV2Route = (routeName?: string): routeName is V2Route => {
    return Object.values(V2_ROUTES).includes(routeName as V2Route);
};

/**
 * Check if a route belongs to Industry Analysis module
 */
export const isIndustryAnalysisRoute = (stateName?: string): boolean => {
    if (!stateName) {
        return false;
    }

    return (
        stateName.includes("marketresearch") ||
        stateName.includes("market_research") ||
        stateName.includes("industryanalysis") ||
        stateName.includes("digitalsuite_marketresearch")
    );
};

/**
 * Determines whether compare button should be enabled
 * Enabled only on routes that actually support comparison functionality
 */
export const shouldEnableCompareButton = (routeName?: string): boolean => {
    if (!routeName) {
        return false;
    }

    // Enable for V2 routes that support comparison (exclude players, segmentation and demand trends)
    return (
        isV2Route(routeName) &&
        routeName !== V2_ROUTES.players &&
        routeName !== V2_ROUTES.segmentation &&
        routeName !== V2_ROUTES.demandTrends
    );
};

/**
 * Checks if any of the provided categories contain LOB segments
 * @param categories - Array of category parameter strings
 * @returns true if any category contains LOB segments, false otherwise
 */
export const hasLobSegments = (categories: string[]): boolean => {
    return categories.some((categoryParam: string) => {
        try {
            const categoryObject = categoryService.categoryQueryParamToCategoryObject(categoryParam);

            // Check if any segments in the category are LOB segments
            if (categoryObject?.segments) {
                return hasLinesOfBusinessSegments(categoryObject.segments);
            }

            return false;
        } catch {
            // If category parsing fails, assume no LOB segments
            return false;
        }
    });
};

/**
 * Allowed duration presets for LOB (Lines of Business) segments
 * LOB segments have data availability starting from LobsFirstAvailableDate (configured in swSettings)
 */
const LOB_ALLOWED_DURATION_PRESETS = ["28d", "1m", "3m", "6m", "12m", "18m", "24m", "25m"];

/**
 * Fallback date for LOB first available date if not configured in swSettings
 */
const LOB_FIRST_AVAILABLE_DATE_FALLBACK = "2023-11-01";

/**
 * Gets the first available date for LOB segments from swSettings configuration
 * Falls back to a hardcoded date if not configured
 */
export const getLobsFirstAvailableDate = (): dayjs.Dayjs => {
    try {
        const lobsFirstAvailableDate = swSettings.components["MarketPlayers"]?.resources?.LobsFirstAvailableDate;
        if (lobsFirstAvailableDate) {
            return dayjs.utc(lobsFirstAvailableDate);
        }
    } catch {
        // If accessing swSettings fails, use fallback
    }
    return dayjs.utc(LOB_FIRST_AVAILABLE_DATE_FALLBACK);
};

/**
 * Default duration to use when LOB segments are detected and current duration is invalid
 */
const LOB_DEFAULT_DURATION = "12m";

/**
 * Checks if a duration is allowed for LOB segments
 * - Preset durations must be in the allowed presets list AND their start date must be >= LobsFirstAvailableDate
 * - Custom date ranges must have start date >= LobsFirstAvailableDate
 */
const isLobDurationAllowed = (duration: string | undefined): boolean => {
    if (!duration) {
        return false;
    }

    const lobFirstAvailableDate = getLobsFirstAvailableDate();

    // For custom date ranges, check if start date is within allowed range
    try {
        const durationData = DurationService.getDurationData(duration);
        const durationStartDate = dayjs(durationData.raw.from);

        // Duration is allowed if start date is on or after LOB first available date
        return (
            durationStartDate.isSame(lobFirstAvailableDate, "day") ||
            durationStartDate.isAfter(lobFirstAvailableDate, "day")
        );
    } catch {
        // If parsing fails, consider it not allowed
        return false;
    }
};

export const shouldShowComparisonBlankState = (routeName?: string, categoryParam?: string | string[]): boolean => {
    // Show blank state when on a non-comparison route with multiple categories
    if (!routeName || !categoryParam) {
        return false;
    }

    const categories = Array.isArray(categoryParam) ? categoryParam : [categoryParam];
    const hasMultipleCategories = categories.length > 1;

    if (!hasMultipleCategories) {
        return false;
    }

    // Routes that don't support comparison should show blank state with multiple categories
    return (
        routeName === LEGACY_ROUTES.players ||
        routeName === V2_ROUTES.players ||
        routeName === LEGACY_ROUTES.segmentation ||
        routeName === V2_ROUTES.segmentation ||
        routeName === LEGACY_ROUTES.demandTrends ||
        routeName === V2_ROUTES.demandTrends
    );
};

/**
 * Custom date picker preset resolver for V2 Industry Analysis routes
 * Filters available presets based on LobsFirstAvailableDate configuration
 *
 * Returns filtered presets based on LobsFirstAvailableDate if any category contains LOB segments,
 * otherwise returns null to allow normal behavior
 */
export const resolveV2DatePickerPreset = (): string[] | null => {
    const params = proNavigator.getParams();
    const { category } = params as { category?: string | string[] };

    if (!category) {
        return null;
    }

    const categoryArray = Array.isArray(category) ? category : [category];

    if (!hasLobSegments(categoryArray)) {
        return null;
    }

    // Filter presets based on LobsFirstAvailableDate
    const lobFirstAvailableDate = getLobsFirstAvailableDate();
    const currentEndOfMonth = dayjs().endOf("month");

    return LOB_ALLOWED_DURATION_PRESETS.filter((preset) => {
        // For day-based presets (e.g., "28d"), always include them
        if (preset.endsWith("d")) {
            return true;
        }

        // For month-based presets, check if start date would be >= LobsFirstAvailableDate
        if (preset.endsWith("m")) {
            const months = parseInt(preset);
            const presetStartDate = currentEndOfMonth.subtract(months, "month").startOf("month");
            return (
                presetStartDate.isSame(lobFirstAvailableDate, "day") ||
                presetStartDate.isAfter(lobFirstAvailableDate, "day")
            );
        }

        return true;
    });
};
